import { Box, Button, Icon, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import {
  CompensationsPayrollConceptFragment,
  CompensationsPayrollConceptsDocument,
  PayrollUpdateConceptDocument,
  PayrollValueObjectsConceptCategoryEnum,
  PayrollValueObjectsConceptUnitTypeEnum,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import SelectField from 'modules/core/components/SelectField'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  DEFAULT_CONCEPT_CATEGORY,
  DEFAULT_CONCEPT_UNIT_TYPE,
  PAYROLL_CONCEPT_CATEGORIES,
  payrollConceptCategoriesOptions,
  payrollConceptUnitTypeOptions,
} from 'modules/payroll_bundle/lib/constants'

interface ConceptForm {
  id: number
  category: PayrollValueObjectsConceptCategoryEnum | undefined | null
  name: string
  label: string
  description?: string | undefined | null
  unitType: PayrollValueObjectsConceptUnitTypeEnum
}

interface EditConceptModalProps {
  concept: CompensationsPayrollConceptFragment
  readonly?: boolean
  onSuccess: () => void
  onClose: () => void
  legacyPayroll?: boolean
}

const EditConceptModal = ({
  concept,
  readonly,
  onSuccess,
  onClose,
  legacyPayroll = false,
}: EditConceptModalProps) => {
  const [open, toggle] = useToggle(false)
  const [update] = useMutation(PayrollUpdateConceptDocument, {
    extractorFn: (result) => result.payrollMutations.updatePayrollConcept,
  })

  const categoryOptions = [
    ...(concept.category
      ? []
      : [
          {
            label: i18n.t('compensations.settings.concepts.uncategorized'),
            value: null,
          },
        ]),
    ...payrollConceptCategoriesOptions(),
  ]

  const form = useForm<ConceptForm>({
    defaultValues: {
      id: concept.id,
      category: concept.category,
      name: concept.translatedName ?? '',
      label: concept.label,
      description: concept.description ?? '',
      unitType: concept.unitType ?? DEFAULT_CONCEPT_UNIT_TYPE,
    },
    validate(values) {
      const errors: FormErrors<ConceptForm> = {}

      if (values.name === '') {
        errors.name = i18n.t('common.validations.empty')
      }

      if (!values.category || !PAYROLL_CONCEPT_CATEGORIES.includes(values.category)) {
        errors.category = i18n.t('common.validations.empty')
      }

      return errors
    },
    async onSubmit(values, { setErrors }) {
      const result = await update({
        variables: {
          id: values.id,
          label: readonly ? values.label : values.name,
          description: values.description,
          category: values.category ?? DEFAULT_CONCEPT_CATEGORY,
          unitType: values.unitType,
        },
        refetchQueries: [CompensationsPayrollConceptsDocument],
      })

      if (!legacyPayroll) {
        track(trackingEvents.compensations.updatePayrollConcept, {
          id: values.id,
          category: values.category ?? DEFAULT_CONCEPT_CATEGORY,
        })
      }

      const handled = handleResult(result, (errors) => setErrors(errors))
      if (handled.success && handled.data.concept) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onSuccess()
      }

      return handled
    },
  })

  return (
    <Modal
      illustration={'edit-file'}
      title={i18n.t('compensations.settings.concepts.edit_concept')}
      description={i18n.t('compensations.settings.concepts.edit_concept_description')}
      onClose={onClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting || !form.isValid || !form.values.category}>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s24">
        <SelectField
          label={i18n.t('compensations.settings.concepts.form.category')}
          value={form.values.category || ''}
          mandatory
          readonly={readonly}
          onChange={(value: string) => form.setFieldValue('category', value)}
          options={categoryOptions}
        />
        <TextField
          label={i18n.t('compensations.settings.concepts.form.name')}
          value={form.values.name}
          onChange={(value: string) => form.setFieldValue('name', value)}
          mandatory
          error={form.touched.name ? form.errors.name : undefined}
          readonly={readonly}
        />
        {readonly && (
          <Box gap="s2">
            <Box flexDirection="row" gap="s4" alignItems="center">
              <Text color="grey1000" weight="medium" size="200">
                {i18n.t('compensations.settings.concepts.form.label')}
              </Text>
              <Tooltip.Alt
                attachment="bottom center"
                open={open}
                toggle={toggle}
                label={i18n.t('compensations.settings.concepts.form.label_tooltip')}
              >
                <Icon.Medium icon="info-circle-line" />
              </Tooltip.Alt>
            </Box>
            <TextField
              value={form.values.label}
              onChange={(value: string) => form.setFieldValue('label', value)}
              error={form.touched.label ? form.errors.label : undefined}
            />
          </Box>
        )}
        {!legacyPayroll && (
          <SelectField
            label={i18n.t('compensations.settings.concepts.form.unit_type')}
            value={form.values.unitType}
            onChange={(value: string) => form.setFieldValue('unitType', value)}
            mandatory
            options={payrollConceptUnitTypeOptions()}
            readonly={readonly}
          />
        )}
        <TextArea
          label={i18n.t('compensations.settings.concepts.form.description')}
          maxLength={250}
          minRows={3}
          width={400}
          value={form.values.description || ''}
          onChange={(value: string) => form.setFieldValue('description', value)}
          readonly={readonly}
        />
      </Box>
    </Modal>
  )
}

export default EditConceptModal
